import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Target,
  Award,
  BarChart3,
  LineChart,
  Activity,
  Zap,
  BookOpen,
  Star,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  Eye,
  ChevronRight,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Trophy,
  Flame,
  Brain,
  Timer,
  Gauge,
  PlayCircle,
  PauseCircle
} from 'lucide-react';
import { useAppSelector } from '../store';

// Interfaces
interface StudySession {
  id: string;
  deckId: string;
  deckName: string;
  date: string;
  totalCards: number;
  correctAnswers: number;
  wrongAnswers: number;
  timeSpent: number; // seconds
  accuracy: number;
  xpEarned: number;
  badges: string[];
}

interface ProgressMetrics {
  totalStudySessions: number;
  totalCardsStudied: number;
  averageAccuracy: number;
  totalTimeSpent: number; // seconds
  totalXpEarned: number;
  currentStreak: number;
  longestStreak: number;
  improvementTrend: 'up' | 'down' | 'stable';
  trendPercentage: number;
  strongestDeck: string;
  weakestDeck: string;
  recentSessions: StudySession[];
}

interface DeckProgress {
  deckId: string;
  deckName: string;
  totalCards: number;
  masteredCards: number;
  studiedCards: number;
  accuracy: number;
  lastStudied: string;
  timeSpent: number;
  sessionsCount: number;
}

const ProgressReport: React.FC = () => {
  const [studySessions, setStudySessions] = useState<StudySession[]>([]);
  const [metrics, setMetrics] = useState<ProgressMetrics | null>(null);
  const [deckProgress, setDeckProgress] = useState<DeckProgress[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  const [activeView, setActiveView] = useState<'overview' | 'decks' | 'trends' | 'achievements'>('overview');
  const [isLoading, setIsLoading] = useState(true);

  const { decks } = useAppSelector(state => state.flashcard);

  // Demo data - gerçek uygulamada Firebase'den gelecek
  useEffect(() => {
    const generateDemoData = () => {
      const demoSessions: StudySession[] = [
        {
          id: '1',
          deckId: 'deck1',
          deckName: 'Matematik Formülleri',
          date: '2024-01-20T10:30:00Z',
          totalCards: 25,
          correctAnswers: 20,
          wrongAnswers: 5,
          timeSpent: 900, // 15 dakika
          accuracy: 80,
          xpEarned: 150,
          badges: ['perfect_session']
        },
        {
          id: '2',
          deckId: 'deck2',
          deckName: 'İngilizce Kelimeler',
          date: '2024-01-19T14:15:00Z',
          totalCards: 30,
          correctAnswers: 25,
          wrongAnswers: 5,
          timeSpent: 1200, // 20 dakika
          accuracy: 83.3,
          xpEarned: 180,
          badges: ['speed_master']
        },
        {
          id: '3',
          deckId: 'deck1',
          deckName: 'Matematik Formülleri',
          date: '2024-01-18T16:45:00Z',
          totalCards: 20,
          correctAnswers: 15,
          wrongAnswers: 5,
          timeSpent: 720, // 12 dakika
          accuracy: 75,
          xpEarned: 120,
          badges: []
        },
        {
          id: '4',
          deckId: 'deck3',
          deckName: 'Tarih Olayları',
          date: '2024-01-17T09:20:00Z',
          totalCards: 35,
          correctAnswers: 28,
          wrongAnswers: 7,
          timeSpent: 1500, // 25 dakika
          accuracy: 80,
          xpEarned: 200,
          badges: ['consistency_king']
        },
        {
          id: '5',
          deckId: 'deck2',
          deckName: 'İngilizce Kelimeler',
          date: '2024-01-16T11:10:00Z',
          totalCards: 28,
          correctAnswers: 22,
          wrongAnswers: 6,
          timeSpent: 1080, // 18 dakika
          accuracy: 78.6,
          xpEarned: 160,
          badges: []
        }
      ];

      setStudySessions(demoSessions);
      calculateMetrics(demoSessions);
      calculateDeckProgress(demoSessions);
      setIsLoading(false);
    };

    generateDemoData();
  }, []);

  const calculateMetrics = (sessions: StudySession[]) => {
    if (sessions.length === 0) return;

    const sortedSessions = sessions.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    // Temel metrikler
    const totalCardsStudied = sessions.reduce((sum, session) => sum + session.totalCards, 0);
    const totalCorrect = sessions.reduce((sum, session) => sum + session.correctAnswers, 0);
    const averageAccuracy = (totalCorrect / totalCardsStudied) * 100;
    const totalTimeSpent = sessions.reduce((sum, session) => sum + session.timeSpent, 0);
    const totalXpEarned = sessions.reduce((sum, session) => sum + session.xpEarned, 0);

    // Trend hesaplama (son 5 oturum vs önceki 5 oturum)
    const recentSessions = sortedSessions.slice(-5);
    const previousSessions = sortedSessions.slice(-10, -5);
    
    let improvementTrend: 'up' | 'down' | 'stable' = 'stable';
    let trendPercentage = 0;
    
    if (previousSessions.length > 0) {
      const recentAvg = recentSessions.reduce((sum, s) => sum + s.accuracy, 0) / recentSessions.length;
      const previousAvg = previousSessions.reduce((sum, s) => sum + s.accuracy, 0) / previousSessions.length;
      trendPercentage = ((recentAvg - previousAvg) / previousAvg) * 100;
      
      if (trendPercentage > 2) improvementTrend = 'up';
      else if (trendPercentage < -2) improvementTrend = 'down';
    }

    // Deste performansları
    const deckPerformances: { [key: string]: { accuracy: number; sessions: number } } = {};
    sessions.forEach(session => {
      if (!deckPerformances[session.deckName]) {
        deckPerformances[session.deckName] = { accuracy: 0, sessions: 0 };
      }
      deckPerformances[session.deckName].accuracy += session.accuracy;
      deckPerformances[session.deckName].sessions += 1;
    });

    Object.keys(deckPerformances).forEach(deckName => {
      deckPerformances[deckName].accuracy /= deckPerformances[deckName].sessions;
    });

    const strongestDeck = Object.keys(deckPerformances).reduce((a, b) => 
      deckPerformances[a].accuracy > deckPerformances[b].accuracy ? a : b
    );
    
    const weakestDeck = Object.keys(deckPerformances).reduce((a, b) => 
      deckPerformances[a].accuracy < deckPerformances[b].accuracy ? a : b
    );

    // Streak hesaplama (basit versiyon)
    const currentStreak = 5; // Demo değer
    const longestStreak = 8; // Demo değer

    setMetrics({
      totalStudySessions: sessions.length,
      totalCardsStudied,
      averageAccuracy,
      totalTimeSpent,
      totalXpEarned,
      currentStreak,
      longestStreak,
      improvementTrend,
      trendPercentage: Math.abs(trendPercentage),
      strongestDeck,
      weakestDeck,
      recentSessions: recentSessions
    });
  };

  const calculateDeckProgress = (sessions: StudySession[]) => {
    const deckStats: { [key: string]: DeckProgress } = {};

    sessions.forEach(session => {
      if (!deckStats[session.deckId]) {
        deckStats[session.deckId] = {
          deckId: session.deckId,
          deckName: session.deckName,
          totalCards: session.totalCards,
          masteredCards: 0,
          studiedCards: session.totalCards,
          accuracy: session.accuracy,
          lastStudied: session.date,
          timeSpent: session.timeSpent,
          sessionsCount: 1
        };
      } else {
        deckStats[session.deckId].accuracy = 
          (deckStats[session.deckId].accuracy * deckStats[session.deckId].sessionsCount + session.accuracy) / 
          (deckStats[session.deckId].sessionsCount + 1);
        deckStats[session.deckId].timeSpent += session.timeSpent;
        deckStats[session.deckId].sessionsCount += 1;
        if (new Date(session.date) > new Date(deckStats[session.deckId].lastStudied)) {
          deckStats[session.deckId].lastStudied = session.date;
        }
      }
    });

    // Mastered cards hesaplama (accuracy > 90% olanlar)
    Object.keys(deckStats).forEach(deckId => {
      deckStats[deckId].masteredCards = Math.floor(
        (deckStats[deckId].accuracy / 100) * deckStats[deckId].totalCards
      );
    });

    setDeckProgress(Object.values(deckStats));
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}s ${minutes}dk`;
    }
    return `${minutes}dk`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getAccuracyColor = (accuracy: number): string => {
    if (accuracy >= 90) return 'text-green-600';
    if (accuracy >= 80) return 'text-blue-600';
    if (accuracy >= 70) return 'text-yellow-600';
    if (accuracy >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  const getAccuracyBgColor = (accuracy: number): string => {
    if (accuracy >= 90) return 'bg-green-100 dark:bg-green-900/20';
    if (accuracy >= 80) return 'bg-blue-100 dark:bg-blue-900/20';
    if (accuracy >= 70) return 'bg-yellow-100 dark:bg-yellow-900/20';
    if (accuracy >= 60) return 'bg-orange-100 dark:bg-orange-900/20';
    return 'bg-red-100 dark:bg-red-900/20';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4 text-blue-500" size={48} />
          <p className="text-gray-600 dark:text-gray-400">İlerleme raporu yükleniyor...</p>
        </div>
      </div>
    );
  }

  const renderOverview = () => (
    <div className="space-y-8">
      {/* Özet Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Toplam Çalışma Oturumu */}
        <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <PlayCircle className="text-white" size={24} />
              </div>
              <Activity className="text-blue-500" size={20} />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {metrics?.totalStudySessions || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Toplam Oturum
            </div>
          </div>
        </div>

        {/* Ortalama Başarı */}
        <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-500/10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                <Target className="text-white" size={24} />
              </div>
              {metrics?.improvementTrend === 'up' ? (
                <TrendingUp className="text-green-500" size={20} />
              ) : metrics?.improvementTrend === 'down' ? (
                <TrendingDown className="text-red-500" size={20} />
              ) : (
                <Activity className="text-gray-500" size={20} />
              )}
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {metrics?.averageAccuracy.toFixed(1) || '0'}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Ortalama Başarı
            </div>
            {metrics && (
              <div className={`text-xs mt-2 ${
                metrics.improvementTrend === 'up' ? 'text-green-600' : 
                metrics.improvementTrend === 'down' ? 'text-red-600' : 'text-gray-600'
              }`}>
                {metrics.improvementTrend === 'up' ? '↗' : 
                 metrics.improvementTrend === 'down' ? '↘' : '→'} %{metrics.trendPercentage.toFixed(1)}
              </div>
            )}
          </div>
        </div>

        {/* Toplam Çalışma Süresi */}
        <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-violet-500/10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-500 rounded-xl flex items-center justify-center">
                <Clock className="text-white" size={24} />
              </div>
              <Timer className="text-purple-500" size={20} />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {formatTime(metrics?.totalTimeSpent || 0)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Toplam Süre
            </div>
          </div>
        </div>

        {/* Kazanılan XP */}
        <div className="relative overflow-hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-red-500/10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                <Zap className="text-white" size={24} />
              </div>
              <Star className="text-orange-500" size={20} />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {metrics?.totalXpEarned || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Toplam XP
            </div>
          </div>
        </div>
      </div>

      {/* Streak ve Performans */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Streak Bilgisi */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Flame className="text-orange-500" size={20} />
              Çalışma Serisi
            </h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl">
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Mevcut Seri</div>
                <div className="text-2xl font-bold text-orange-600">{metrics?.currentStreak || 0} gün</div>
              </div>
              <Flame className="text-orange-500" size={32} />
            </div>
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl">
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">En Uzun Seri</div>
                <div className="text-2xl font-bold text-yellow-600">{metrics?.longestStreak || 0} gün</div>
              </div>
              <Trophy className="text-yellow-500" size={32} />
            </div>
          </div>
        </div>

        {/* En İyi ve En Zayıf Desteler */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <BarChart3 className="text-blue-500" size={20} />
              Deste Performansı
            </h3>
          </div>
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">En Güçlü Deste</div>
                  <div className="font-semibold text-green-700 dark:text-green-400">
                    {metrics?.strongestDeck || 'Henüz veri yok'}
                  </div>
                </div>
                <CheckCircle className="text-green-500" size={24} />
              </div>
            </div>
            <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Geliştirilmesi Gereken</div>
                  <div className="font-semibold text-red-700 dark:text-red-400">
                    {metrics?.weakestDeck || 'Henüz veri yok'}
                  </div>
                </div>
                <AlertTriangle className="text-red-500" size={24} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Son Çalışma Oturumları */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Activity className="text-blue-500" size={20} />
            Son Çalışma Oturumları
          </h3>
          <button className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1">
            Tümünü Gör
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="space-y-3">
          {metrics?.recentSessions.slice(0, 5).map((session) => (
            <div key={session.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getAccuracyBgColor(session.accuracy)}`}>
                  <BookOpen className={getAccuracyColor(session.accuracy)} size={20} />
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">{session.deckName}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(session.date)} • {formatTime(session.timeSpent)}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className={`font-semibold ${getAccuracyColor(session.accuracy)}`}>
                  {session.accuracy.toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {session.correctAnswers}/{session.totalCards}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderDecks = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {deckProgress.map((deck) => (
          <div key={deck.deckId} className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getAccuracyBgColor(deck.accuracy)}`}>
                <BookOpen className={getAccuracyColor(deck.accuracy)} size={24} />
              </div>
              <div className="text-right">
                <div className={`text-lg font-bold ${getAccuracyColor(deck.accuracy)}`}>
                  {deck.accuracy.toFixed(1)}%
                </div>
              </div>
            </div>
            
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{deck.deckName}</h3>
            
            <div className="space-y-3">
              {/* İlerleme Çubuğu */}
              <div>
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                  <span>İlerleme</span>
                  <span>{deck.masteredCards}/{deck.totalCards}</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(deck.masteredCards / deck.totalCards) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              {/* İstatistikler */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-600 dark:text-gray-400">Oturum</div>
                  <div className="font-semibold text-gray-900 dark:text-white">{deck.sessionsCount}</div>
                </div>
                <div>
                  <div className="text-gray-600 dark:text-gray-400">Süre</div>
                  <div className="font-semibold text-gray-900 dark:text-white">{formatTime(deck.timeSpent)}</div>
                </div>
              </div>
              
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Son çalışma: {formatDate(deck.lastStudied)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              İlerleme Raporu
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Flashcard çalışma performansınızı detaylı olarak inceleyin
            </p>
          </div>
          
          <div className="flex items-center gap-4 mt-4 md:mt-0">
            {/* Zaman Filtresi */}
            <select 
              value={selectedPeriod} 
              onChange={(e) => setSelectedPeriod(e.target.value as any)}
              className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="7d">Son 7 Gün</option>
              <option value="30d">Son 30 Gün</option>
              <option value="90d">Son 90 Gün</option>
              <option value="all">Tüm Zamanlar</option>
            </select>
            
            {/* Export Button */}
            <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
              <Download size={16} />
              Dışa Aktar
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl mb-8">
          {[
            { id: 'overview', label: 'Genel Bakış', icon: Eye },
            { id: 'decks', label: 'Desteler', icon: BookOpen },
            { id: 'trends', label: 'Trendler', icon: LineChart },
            { id: 'achievements', label: 'Başarılar', icon: Trophy }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeView === tab.id
                    ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {activeView === 'overview' && renderOverview()}
        {activeView === 'decks' && renderDecks()}
        {activeView === 'trends' && (
          <div className="text-center py-12">
            <LineChart className="mx-auto mb-4 text-gray-400" size={48} />
            <p className="text-gray-600 dark:text-gray-400">Trend analizi yakında eklenecek...</p>
          </div>
        )}
        {activeView === 'achievements' && (
          <div className="text-center py-12">
            <Trophy className="mx-auto mb-4 text-gray-400" size={48} />
            <p className="text-gray-600 dark:text-gray-400">Başarı sistemi yakında eklenecek...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressReport;